import { Form } from "@/components/ui/form";
import { SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { FileText } from "lucide-react";
import { SectionCard } from "@/components/ui/section-card";
import { useRelacaoPagamentosForm } from "../../hooks/use-relacao-pagamentos-form";
import {
  FormCpfCnpjField,
  FormCurrencyField,
  FormDateField,
  FormFileField,
  FormSectionDivider,
  FormSelectField,
  FormTextareaField,
  FormTextField
} from "@/components/form";
import { fasesOrcamento, historyItems } from "../../constants/relacao-pagamentos.contants";
import { useEffect, useState } from "react";
import { FormButtons } from "./form-buttons";
import { IProject } from "@/interfaces/project.interface";

export default function FormRelacaoPagamentos({
  projetoSelecionado,
  projects,
  onClose
}: {
  projetoSelecionado: IProject | null,
  projects?: { id: string; name: string, salic_number: string }[]
  onClose?: () => void
}) {
  const [faseOrcamento, setFaseOrcamento] = useState<number>(0)
  const [contratoId, setContratoId] = useState<number>(0)

  const params = {
    movieId: projetoSelecionado?.id || 0,
    budgetId: projetoSelecionado?.budgets?.[0]?.id || 0,
    faseOrcamento
  }

  const {
    form,
    person,
    isPending,
    contratos,
    budgetItems,
    equipe,
    handleFileUpload,
    navigate,
    onSubmit
  } = useRelacaoPagamentosForm(params.movieId, params.budgetId, params.faseOrcamento);

  const bankAccounts = person?.bank_accounts ?? []
  const watchedContractId = form.watch("id_staff_contract")
  const watchedBank = form.watch("bank")
  const watchedCreditor = form.watch("creditor")
  const watchedProject = form.watch("project")

  useEffect(() => {
    if (watchedProject) {
      const selectedProject = projects?.find(p => p.id === watchedProject)
      form.setValue("salic", selectedProject?.salic_number || "")
    }
  }, [watchedProject])

  useEffect(() => {
    const selectedContract = contratos?.find(c => c.id.toString() === watchedContractId)
    if (selectedContract) {
      setContratoId(selectedContract.id)
    }
  }, [watchedContractId, contratos])

  useEffect(() => {
    if (!watchedBank) return
    const selectedAccount = bankAccounts.find(acc => acc.bank_name === watchedBank)
    if (selectedAccount) {
      form.setValue("agency", selectedAccount.agency === null ? "NaN" : selectedAccount.agency.toString())
      form.setValue("account", selectedAccount.bank_account === null ? "NaN" : selectedAccount.bank_account)
    }
  }, [watchedBank])

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "creditor") {
        const selectedPerson = equipe?.find(p => p.id.toString() === value.creditor)
        form.setValue("cpfCnpj", selectedPerson?.pjDocument || "")
      }
    })
    return () => subscription.unsubscribe()
  }, [watchedCreditor])

  return (
    <SectionCard className="p-6 md:p-8 ">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
          <h3 className="text-xl font-bold text-white">Novo Lançamento</h3>

          <div className="flex flex-col gap-6">
            {/* Identificação do Projeto */}
            <section className="flex flex-col gap-4">
              <FormSectionDivider title="identificação do projeto" />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormSelectField name="id_staff_contract" label="Contrato" placeholder="Selecione o contrato" required>
                  {(!contratos || contratos.length === 0) && (
                    <SelectItem key={0} value="-" disabled>
                      Nenhum contrato disponível
                    </SelectItem>
                  )}

                  {contratos?.map((p, idx) => (
                    <SelectItem key={idx} value={p?.id?.toString()}>
                      {p?.staffUser?.person.name || idx}
                    </SelectItem>
                  ))}
                </FormSelectField>

                <FormFileField
                  name="fiscal_note_url"
                  label="Upload de Documento Fiscal"
                  placeholder="Upload PDF ou XML"
                  onFileSelect={(file) => handleFileUpload(file, contratoId)}
                  className="md:col-span-2"
                  tooltipContent="Selecione um contrato para enviar o documento"
                  disabled={!contratos || contratos.length === 0}
                />

                <FormSelectField name="project" label="Projeto" placeholder="Selecione o projeto" required>
                  {projects?.map((p, idx) => (
                    <SelectItem key={idx} value={p.id}>{p.name}</SelectItem>
                  ))}
                </FormSelectField>

                <FormTextField name="salic" label="SALIC" placeholder="000000-0" disabled required />
                <FormTextField name="proponent" label="Proponente" required disabled />

                <FormSelectField name="bank" label="Banco" placeholder="Nome do Banco" required>
                  {(!bankAccounts || bankAccounts.length === 0) && (
                    <SelectItem key={0} value="-" disabled>
                      Nenhum banco disponível
                    </SelectItem>
                  )}

                  {bankAccounts.length > 0 && bankAccounts.map((acc, idx) => {
                    if (!acc?.bank_name) return null
                    return (
                      <SelectItem key={idx} value={acc?.bank_name?.toString()}>
                        {acc?.bank_name}
                      </SelectItem>
                    )
                  })}
                </FormSelectField>

                <FormSelectField name="agency" label="Agência" placeholder="Selecione a agência" required disabled>
                  {bankAccounts.length > 0 && bankAccounts.map((acc, idx) => {
                    if (!acc?.agency) return null
                    return (
                      <SelectItem key={idx} value={acc?.agency?.toString()}>
                        {acc?.agency}
                      </SelectItem>
                    )
                  })}
                </FormSelectField>

                <FormSelectField name="account" label="Conta" placeholder="Selecione a conta" required disabled>
                  {bankAccounts.length > 0 && bankAccounts?.map((acc, idx) => {
                    if (!acc?.bank_account || !acc?.bank_account) return null
                    return (
                      <SelectItem key={idx} value={acc?.bank_account}>
                        {acc?.bank_account}
                      </SelectItem>
                    )
                  })}
                </FormSelectField>
              </div>
            </section>

            {/* Identificação do Lançamento */}
            <section className="flex flex-col gap-4">
              <FormSectionDivider title="identificação do lançamento" />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormSelectField name="creditor" label="Credor" placeholder="Selecione o credor" required>
                  {equipe?.map((p, idx) => (
                    p.id != person?.id && <SelectItem key={idx} value={p.id.toString()}>{p.name}</SelectItem>
                  ))}
                </FormSelectField>
                <FormCpfCnpjField name="cpfCnpj" label="CNPJ" placeholder="Digite o CNPJ" disabled required />
                <FormDateField name="payment_date" label="Data de Pagamento" placeholder="Data de pagamento" required />
                <FormTextField name="emission_type" label="Tipo de Documento" placeholder="Ex: NF, Recibo..." required />
                <FormTextField name="emission_number" label="Nº do Documento" placeholder="0000000-000" required />
                <FormDateField name="emission_date" label="Data de Emissão" placeholder="Data de emissão" required />
                <FormCurrencyField name="amount" label="Valor" required />

                <FormSelectField
                  name="fase-orcamento"
                  label="Fase do Orçamento"
                  placeholder="Selecione a fase do orçamento"
                  onChange={(value) => setFaseOrcamento(Number(value))}
                  required
                >
                  {fasesOrcamento?.map((p, idx) => (
                    <SelectItem key={idx} value={p.id.toString()}>{p.step}</SelectItem>
                  ))}
                </FormSelectField>

                <FormSelectField
                  name="budget_item_id"
                  label="Sub-item do Orçamento"
                  placeholder="Selecione o sub-item do orçamento"
                  required
                >
                  {(!budgetItems || budgetItems?.length === 0) && (
                    <SelectItem key={0} value="-" disabled>
                      Nenhum sub-item disponível
                    </SelectItem>
                  )}

                  {budgetItems?.map((p, idx) => (
                    <SelectItem key={idx} value={p.id.toString()}>
                      {p.description}
                    </SelectItem>
                  ))}
                </FormSelectField>
              </div>
            </section>

            <div className="h-px bg-gray-700/50 w-full" />

            {/* Observações */}
            <FormTextareaField
              name="notes"
              label="Observações"
              placeholder="Digite observações relevantes..."
            />

            <div className="h-px bg-gray-700/50 w-full" />

            {/* Histórico */}
            <div className="flex flex-col gap-3">
              <Label className="text-gray-200 text-sm font-semibold">Histórico</Label>
              <div className="border border-gray-700 rounded-lg p-4 max-h-[160px] overflow-y-auto flex flex-col gap-3">
                {historyItems.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 py-1">
                    <FileText className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-400 text-xs leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Ações */}
            <FormButtons navigate={navigate} isValid={form.formState.isValid} isPending={isPending} onClose={onClose} />
          </div>
        </form>
      </Form>
    </SectionCard>
  );
}